const DAY_MS = 86_400_000;

export const BILLING_STATUSES = ['trialing', 'active', 'past_due', 'canceled', 'manual'];
export const ALLOWED_STATUSES = new Set(['active', 'trialing', 'manual']);
export const BILLING_EMAIL = 'billing@censuszen.com';

export const PLANS = {
    agency_500: { label: 'Agency', priceMonthly: 500, seatLimit: 5, caseAllowance: 150 },
};

export function toMillis(input) {
    if (input == null) return null;
    if (typeof input === 'number') return input;
    if (typeof input === 'string') { const ms = Date.parse(input); return Number.isNaN(ms) ? null : ms; }
    if (input instanceof Date) return input.getTime();
    if (typeof input === 'object' && typeof input.toMillis === 'function') return input.toMillis();
    if (typeof input === 'object' && typeof input.seconds === 'number') return input.seconds * 1000;
    return null;
}

/**
 * Mirrors backend effective_status() in src/censuszen/facility/billing.py:
 * a future override_until wins; a trial with a past OR MISSING trial_ends_at is
 * blocked. The backend names that state "trial_expired"; the frontend folds it
 * into "past_due" (same gate outcome) and summarizeBilling() sets trialExpired
 * so the UI can label it "Trial ended". The 402 body's `status` field still
 * carries the backend name.
 */
export function effectiveStatus(billing, nowMs = Date.now()) {
    if (!billing || !billing.status) return null;
    const override = toMillis(billing.override_until);
    if (override != null && override > nowMs) return 'active';
    if (billing.status === 'trialing') {
        const ends = toMillis(billing.trial_ends_at);
        if (ends == null || ends <= nowMs) return 'past_due';
    }
    return billing.status;
}

export function isBillingAllowed(billing, nowMs = Date.now()) {
    const s = effectiveStatus(billing, nowMs);
    return s != null && ALLOWED_STATUSES.has(s);
}

export function trialDaysLeft(billing, nowMs = Date.now()) {
    const ends = toMillis(billing?.trial_ends_at);
    if (ends == null) return null;
    return Math.max(0, Math.ceil((ends - nowMs) / DAY_MS));
}

export function summarizeBilling(billing, nowMs = Date.now()) {
    const status = effectiveStatus(billing, nowMs);
    const override = toMillis(billing?.override_until);
    return {
        status,
        rawStatus: billing?.status ?? null,
        allowed: status != null && ALLOWED_STATUSES.has(status),
        trialExpired: billing?.status === 'trialing' && status === 'past_due',
        trialDaysLeft: billing?.status === 'trialing' ? trialDaysLeft(billing, nowMs) : null,
        overrideActive: override != null && override > nowMs,
        periodEndMs: toMillis(billing?.current_period_end),
        trialEndMs: toMillis(billing?.trial_ends_at),
        cancelAtPeriodEnd: Boolean(billing?.cancel_at_period_end),
    };
}

const STATUS_LABELS = { active: 'Active', trialing: 'Trial', past_due: 'Payment needed', canceled: 'Canceled', manual: 'Invoiced' };
const STATUS_TONES = { active: 'teal', trialing: 'amber', manual: 'slate', past_due: 'red', canceled: 'red' };

export function statusLabel(status, { trialExpired = false } = {}) {
    if (status === 'past_due' && trialExpired) return 'Trial ended';
    return STATUS_LABELS[status] ?? 'No plan';
}
export function statusTone(status) { return STATUS_TONES[status] ?? 'slate'; }

export function planLabel(plan) {
    if (!plan) return '—';
    if (PLANS[plan]) return PLANS[plan].label;
    return plan.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export function planDetails(plan, billing = {}) {
    const base = PLANS[plan] ?? { priceMonthly: null, seatLimit: null, caseAllowance: null };
    return {
        priceMonthly: base.priceMonthly,
        seatLimit: billing?.seat_limit ?? base.seatLimit,
        caseAllowance: billing?.case_allowance ?? base.caseAllowance,
    };
}

export function seatsUsed(members = [], invites = []) {
    const pending = (invites ?? []).filter((i) => !i?.used).length;
    return (members ?? []).length + pending;
}

export function formatBillingDate(input) {
    const ms = toMillis(input);
    if (ms == null) return '—';
    return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}
